import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import TextClamp from 'react-string-clamp';

import { SEARCH_QUERY_GENERIC } from '../../common/consts';
import { getTitleFromQuery } from '../../common/helpers/searchQuery.utils';
import useI18n from '../../common/hooks/useI18n';
import useSize from '../../common/hooks/useSize';
import {
  SearchOption,
  findSearchOptionByCountyCityAndZone,
} from '../../common/helpers/searchOption.utils';
import { SearchService } from '../../common/hooks/useSearchService';
import { SearchType } from '../../common/model/enums/searchType.enum';

import { Description } from '../shared';

type Props = {
  searchService: SearchService;
  autoCompleteConfirmedValue: string;
  searchOptions: SearchOption[];
  i18nQuery: string;
  className?: string;
};

const MAX_LINES = 3;

const MainText = styled.h1`
  ${(props) => props.theme.font.h2}
  color: ${(props) => props.theme.colors.secondary};
  margin-bottom: 1rem;
`;

const DescriptionContainer = styled.div`
  position: relative;
  overflow: hidden;
`;

const DescriptionTextClamp = styled.div`
  visibility: hidden;
`;

const DescriptionAbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
`;

const ShowMoreButton = styled.div`
  margin-top: 8px;
  text-align: right;
  font-size: 14px;
`;

const DescriptionBottomMargin = styled.div`
  margin-bottom: 16px;
`;

const Title: React.FC<Props> = ({
  searchService,
  autoCompleteConfirmedValue,
  searchOptions,
  i18nQuery,
  className,
}): JSX.Element => {
  const i18n = useI18n();
  const router = useRouter();

  const textClampRef = useRef<HTMLDivElement>(null);
  const descriptionAbsoluteContainerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const textClampSize = useSize(textClampRef);
  const descriptionAbsulteContainerSize = useSize(
    descriptionAbsoluteContainerRef
  );

  const [maxLines, setMaxLines] = useState(MAX_LINES);
  const [
    textClampHeightBeingCollapsed,
    setTextClampHeightBeingCollapsed,
  ] = useState<number>(null);

  const title = useMemo(() => {
    const filter = searchService.getFilter();

    let text = autoCompleteConfirmedValue;
    let searchType: SearchType = SearchType.CLOSED;
    if (filter.mapBounds) {
      searchType = SearchType.MAP_BOUNDS;
    } else if (filter.q) {
      text = filter.q;
      searchType = SearchType.OPEN;
    } else if (filter.mapArea) {
      text = filter.mapArea.id;
      searchType = SearchType.MAP_AREA;
    } else {
      if (router.query.query === SEARCH_QUERY_GENERIC) {
        text = 'Inmobiliaria Núcleo';
        searchType = SearchType.CLOSED;
      }

      let searchOption = findSearchOptionByCountyCityAndZone(
        searchOptions,
        filter.county,
        filter.city,
        filter.zone
      );

      if (searchOption) {
        text = searchOption.text;
        searchType = SearchType.CLOSED;
      }
    }

    let flatType: string | null = null;
    if (filter.types && filter.types.length === 1) {
      flatType = filter.types[0];
    }

    return getTitleFromQuery(text, searchType, flatType, i18n);
  }, [
    autoCompleteConfirmedValue,
    i18n,
    router.query.query,
    searchOptions,
    searchService,
  ]);

  const showMoreButtonVisible = useMemo(() => {
    return (
      textClampHeightBeingCollapsed &&
      descriptionAbsulteContainerSize &&
      textClampHeightBeingCollapsed < descriptionAbsulteContainerSize.height
    );
  }, [descriptionAbsulteContainerSize, textClampHeightBeingCollapsed]);

  const description = useMemo(
    () => i18n.t(`dynamic.searchPageUrls2.${i18nQuery}.description`),
    [i18n, i18nQuery]
  );

  const handleShowMore = useCallback(() => {
    setMaxLines((prev) => (prev === MAX_LINES ? 1000 : MAX_LINES));
  }, []);

  useEffect(() => {
    if (maxLines === MAX_LINES) {
      setTextClampHeightBeingCollapsed(
        textClampSize ? textClampSize.height : null
      );
    }
  }, [maxLines, textClampSize]);

  return (
    <div className={className}>
      <MainText>{title}</MainText>

      {description && (
        <>
          <DescriptionContainer>
            <DescriptionTextClamp as="div" ref={textClampRef}>
              {descriptionRef.current && (
                <Description htmlText={null}>
                  <TextClamp
                    text={descriptionRef.current.innerText}
                    lines={maxLines}
                    element="p"
                  />
                </Description>
              )}
            </DescriptionTextClamp>
            <DescriptionAbsoluteContainer
              as="div"
              ref={descriptionAbsoluteContainerRef}
            >
              <Description htmlText={description} ref={descriptionRef} />
            </DescriptionAbsoluteContainer>
          </DescriptionContainer>

          {showMoreButtonVisible && (
            <ShowMoreButton>
              <a onClick={handleShowMore}>
                <span>
                  {i18n.t(
                    maxLines === MAX_LINES
                      ? 'actions.showMore'
                      : 'actions.showLess'
                  )}
                </span>
              </a>
            </ShowMoreButton>
          )}

          <DescriptionBottomMargin />
        </>
      )}
    </div>
  );
};

export default styled(Title)`
  position: relative;
`;
